import { Manufacturer, Model, Vehicle } from 'api3';
import { Injectable, inject } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class VehicleService {
  translocoService = inject(TranslocoService);
  api3Service = inject(Api3Service);

  public async getFuelTypeTranslation(fuelType: string | undefined): Promise<string | undefined> {
    if (!fuelType) {
      return undefined;
    }

    return new Promise((resolve) => {
      const translationKey = `vehiclesDetails.fuelType.${fuelType.toLowerCase()}`;
      this.translocoService.selectTranslate(translationKey).subscribe({
        next: (translation) => {
          if (translation && translation !== translationKey) {
            resolve(translation);
          }
          resolve(fuelType);
        },
        error: () => {
          resolve(fuelType);
        },
      });
    });
  }

  public async getFuelsString(fuelType: string[] | null | undefined): Promise<string | undefined> {
    if (!fuelType) return undefined;

    const translations = await Promise.all(
      fuelType.map(async (type) => {
        const translation = await this.getFuelTypeTranslation(type);
        return translation;
      }),
    );

    return translations.join(', ');
  }

  public getModelString(model: Model | null | undefined, make: string | null | undefined): string {
    if (!model) {
      return '';
    }

    const modelName = make ? model.name.replace(make, '').trim() : model.name;
    const displayItems = [modelName];

    const yearRange = this.getYearRange({ model });
    if (yearRange) displayItems.push(yearRange);

    return displayItems.join(' ');
  }

  public getTypeString(vehicle: Vehicle | null | undefined, make?: string | null | undefined): string {
    if (!vehicle) return '';

    const typeName = make ? vehicle.name.replace(make, '').trim() : vehicle.name;

    const displayItems = [typeName];

    if (vehicle.engineCode) {
      if (vehicle.engineCode.toLocaleLowerCase() !== 'unknown') displayItems.push(`(${vehicle.engineCode})`);
    }

    if (vehicle.fuelType) {
      const fuelTypes = Array.isArray(vehicle.fuelType) ? vehicle.fuelType : [vehicle.fuelType];
      if (fuelTypes.length > 0) {
        displayItems.push(fuelTypes.join(', '));
      }
    }

    const yearRange = this.getYearRange({ vehicle });
    if (yearRange) displayItems.push(yearRange);

    return displayItems.join(' ');
  }

  public getYearRange(prop: { model?: Model; vehicle?: Vehicle }): string {
    let from;
    let to;

    if (prop.model) {
      from = prop.model.details?.yearOfConstrFrom?.toString();
      to = prop.model.details?.yearOfConstrTo?.toString();
    } else if (prop.vehicle) {
      from = prop.vehicle.madeFrom?.toString();
      to = prop.vehicle.madeUntil?.toString();
    }

    let display = from;
    if (to) {
      display += ` - ${to}`;
    }
    return display || '';
  }

  /**
   * Fallback for not available vehicle images
   * Will most probably fail due to missing carTypeId
   * TODO: Find an alternative way to get vehicle images !!!!!!!!!!!!!!!!!!!
   */
  public getVehicleImage(vehicle: Vehicle): { url: string; alt: string } | undefined {
    if (vehicle.image) return { url: vehicle.image, alt: `Image of ${vehicle.name}` };
    const cartypeId = vehicle.id.carTypeId;
    if (cartypeId) {
      const url = `https://www.haynespro-assets.com/workshop/images/${cartypeId}.svgz`;
      const alt = `Image of ${vehicle.name}`;
      return { url, alt };
    }
    return undefined;
  }

  async getMakes(): Promise<{ data: Manufacturer[] | undefined; error: any }> {
    const { data, error } = await this.api3Service.api.vehicles.getMakes();

    if (error) {
      console.error('[VehicleService#getMakes] Error:', error);
      return { data: undefined, error };
    }

    return { data, error };
  }

  async getModels(makeId: Manufacturer['id']): Promise<{ data: Model[] | undefined; error: any }> {
    const { data, error } = await this.api3Service.api.vehicles.getModels(makeId);

    if (error) {
      console.error('[VehicleService#getModels] Error:', error);
      return { data: undefined, error };
    }

    return { data, error };
  }

  async getVehiclesByModelId(makeId: Manufacturer['id'], modelId: Model['id']): Promise<{ data: Vehicle[] | undefined; error: any }> {
    const { data, error } = await this.api3Service.api.vehicles.getVehiclesByModelId(makeId, modelId);

    if (error) {
      console.error('[VehicleService#getVehiclesByModelId] Error:', error);
      return { data: undefined, error };
    }
    return { data, error };
  }

  async getVehiclesByVIN(vin: string): Promise<{ data: Vehicle[] | undefined; error: any }> {
    const { data, error } = await this.api3Service.api.vehicles.getVehicleByVin(vin);

    if (error) {
      console.error('[VehicleService#getVehicleByVIN] Error:', error);
      return { data: undefined, error };
    }
    return { data, error };
  }

  async getVehiclesByKBA(kba: string): Promise<{ data: Vehicle[] | undefined; error: any }> {
    const { data, error } = await this.api3Service.api.vehicles.getVehicleByKba(kba);

    if (error) {
      console.error('[VehicleService#getVehicleByKBA] Error:', error);
      return { data: undefined, error };
    }
    return { data, error };
  }
}
